import axios from 'axios'
import qs from 'qs'

export default class {

    constructor() {
        this.storagePrefix = ''
        this.cacheTimeout = parseInt(process.env.MIX_API_CACHE_TIMEOUT) || 60 // in seconds
    }

    /**
     * Format data for post body
     *
     * @param data data to transform
     * @private
     */
    _stringify(data) {
        return qs.stringify(data)
    }

    /**
     * GET request
     *
     * @param url URL of request
     * @param params URL params
     * @param fromCache Retrieve from cache (if available) or not
     * @param removeRelatedCache Remove related cache or not
     * @returns {Promise.<*>}
     */
    async get(url, params = {}, fromCache = true, removeRelatedCache = false) {
        try {
            const response = fromCache ? await this.getCachedUrlContent(url, params) : await axios.get(url, {
                headers: {
                    Accept: 'application/json',
                },
                params: {
                    ...params,
                },
            })

            if (removeRelatedCache)
                this.removeCachedRelatedContent()

            return {
                status: response.status,
                data: response.data,
            }
        } catch (error) {
            throw {
                status: error.response.status,
                data: error.response.data,
            }
        }
    }

    /**
     * POST request
     *
     * @param url URL of request
     * @param data DATA to send
     * @returns {Promise.<*>}
     */
    async post(url, data) {
        try {
            let response = null

            if (data instanceof FormData) {
                response = await axios.post(url, data, {
                    headers: {
                        Accept: 'application/json',
                        'Content-Type': 'multipart/form-data',
                    },
                })

            } else {
                response = await axios.post(url, this._stringify(data), {
                    headers: {
                        Accept: 'application/json',
                    },
                })
            }


            this.removeCachedRelatedContent()

            if (!response) {
                throw new Error({
                    status: 404,
                    data: {},
                    errors: {},
                })
            }

            return {
                status: response.status,
                data: response.data,
            }
        } catch (error) {
            throw {
                status: error.response.status,
                data: error.response.data,
                errors: error.response.data.errors,
            }
        }
    }

    /**
     * PUT request
     *
     * @param url URL of request
     * @param data DATA to send
     * @returns {Promise.<*>}
     */
    async put(url, data) {
        try {
            const response = await axios.put(url, this._stringify(data), {
                headers: {
                    Accept: 'application/json',
                },
            })

            this.removeCachedRelatedContent()

            return {
                status: response.status,
                data: response.data,
            }
        } catch (error) {
            throw {
                status: error.response.status,
                data: error.response.data,
                errors: error.response.data.errors,
            }
        }
    }

    /**
     * DELETE request
     *
     * @param url URL of request
     * @param params params to send
     * @returns {Promise.<*>}
     */
    async delete(url, params = {}) {
        try {
            const response = await axios.delete(url, {
                headers: {
                    Accept: 'application/json',
                },
                params: {
                    ...params,
                },
            })

            this.removeCachedRelatedContent()

            return {
                status: response.status,
                data: response.data,
            }
        } catch (error) {
            throw {
                status: error.response.status,
                data: error.response.data,
                errors: error.response.data.errors,
            }
        }
    }

    /**
     *
     * @param url URL of request
     * @param URL params
     * @param expireInSeconds
     * @returns {Promise.<*>}
     */
    async getCachedUrlContent(url, params = {}, expireInSeconds = this.cacheTimeout) {
        let data = null

        // TODO transform cached key (without the prefix) to hash
        const cachedKey = `${this.storagePrefix}${url}?${qs.stringify(params, { sort: this.alphabeticalSort })}`

        const ObjectFromStore = window.localStorage.getItem(cachedKey)

        data = JSON.parse(ObjectFromStore)

        // Il y a des données dans le cache et le cache est expiré
        if (data !== null && data['expireAt'] &&
            new Date(data.expireAt) < (new Date())) {

            window.localStorage.removeItem(cachedKey)

            data = null
        }

        // update cache + set expireAt date
        if (data === null) {
            data = await axios.get(url, {
                headers: {
                    Accept: 'application/json',
                },
                params: {
                    ...params,
                },
            })

            // set expireAt
            data.expireAt = this.getExpireDate(expireInSeconds)

            // stringify
            const objectToStore = JSON.stringify(data)

            // store object
            window.localStorage.setItem(cachedKey, objectToStore)

            return data
        }

        return data
    }

    /**
     * Get an expire date
     *
     * @param expireInSeconds
     * @returns {Date}
     */
    getExpireDate(expireInSeconds) {
        const now = new Date()
        let expireTime = new Date(now)
        expireTime.setSeconds(now.getSeconds() + expireInSeconds)

        return expireTime
    }

    /**
     * Alphabetical Sort Function
     *
     * @param a
     * @param b
     * @returns {number}
     */
    alphabeticalSort(a, b) {
        return a.localeCompare(b)
    }


    /**
     * Remove cached related content (from the same data)
     *
     * @returns {boolean}
     */
    removeCachedRelatedContent() {
        Object.keys(window.localStorage).forEach((element) => {
            if (element.startsWith(this.storagePrefix))
                window.localStorage.removeItem(element)
        })

        return true
    }

}



// WEBPACK FOOTER //
// ./application/assets/js/professional/api/api.js